import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import {
  Button,
  Container,
  Typography,
  Stack,
  CircularProgress,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Chip,
} from "@mui/material";
import Iconify from "src/components/iconify/Iconify";
import { useNavigate } from "react-router-dom";
import { get } from "src/utils/networkUtils";
import useWithUrlTenant from "src/hooks/useTenant";

export const PACK_TYPE_LABELS = {
  0: "Base11",
  17: "DailyPack1",
  18: "AllAccessPassAllStar1",
  19: "AllAccessPassDay1",
  20: "AllAccessPassDay2",
  21: "AllAccessPassDay3",
  22: "AllAccessPassDay4",
  23: "AllAccessPassDay5",
  24: "AllAccessPassDay6",
  36: "OnboardingEmailIncentive3",
};

export default function PackAssetsPage() {
  const navigate = useNavigate();
  const withTenant = useWithUrlTenant();
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const defaultLocale = process.env.REACT_APP_DEFAULT_LOCALE || 'en';

  const loadAssets = () => {
    setLoading(true);
    get(`/admin/packs/assets`)
      .then((res) => {
        setAssets(res.assets || []);
      })
      .catch(() => setAssets([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAssets();
  }, []);

  const handleMenuOpen = (e, asset) => {
    setAnchorEl(e.currentTarget);
    setSelectedAsset(asset);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAsset(null);
  };

  const handleEdit = () => {
    const asset = selectedAsset;
    handleMenuClose();
    if (asset) {
      navigate(withTenant(`/dashboard/pack-assets/${asset.packAssetId}`));
    }
  };

  const parseI18nMap = (i18nValue) => {
    if (!i18nValue) return {};
    if (typeof i18nValue === "string") {
      try {
        return JSON.parse(i18nValue);
      } catch {
        return { en: i18nValue };
      }
    }
    return i18nValue;
  };

  const getI18nPreview = (i18nValue) => {
    const map = parseI18nMap(i18nValue);
    return map?.[defaultLocale] || "—";
  };

  return (
    <>
      <Helmet>
        <title>Pack Assets</title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Typography variant="h4">Pack Assets</Typography>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => navigate(withTenant(`/dashboard/pack-assets/new`))}
          >
            New Asset
          </Button>
        </Stack>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Pack Type</TableCell>
                  <TableCell>Image ({defaultLocale.toUpperCase()})</TableCell>
                  <TableCell>Animation ({defaultLocale.toUpperCase()})</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No pack assets found. Click "New Asset" to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  assets.map((asset) => {
                    const imageMap = parseI18nMap(asset.imageI18n);
                    const animEn = getI18nPreview(asset.animationI18n);
                    return (
                      <TableRow key={asset.packAssetId} hover>
                        <TableCell sx={{ color: "text.secondary" }}>
                          {asset.packAssetId}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              PACK_TYPE_LABELS[asset.packType] ?? asset.packType
                            }
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1.5 }}>
                            <Box
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: imageMap?.en ? "divider" : "grey.200",
                                overflow: "hidden",
                                background: "#f4f6f8",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {imageMap?.[defaultLocale] ? (
                                <img
                                  src={imageMap?.[defaultLocale]}
                                  alt="English pack asset preview"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                  onError={(e) => {
                                    e.target.style.opacity = "0.2";
                                  }}
                                />
                              ) : (
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                >
                                  —
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 220,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            color={
                              animEn !== "—" ? "text.primary" : "text.disabled"
                            }
                          >
                            {animEn}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton onClick={(e) => handleMenuOpen(e, asset)}>
                            <Iconify icon="eva:more-vertical-outline" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Container>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <Iconify icon="eva:edit-outline" width={20} sx={{ mr: 1 }} />
          Edit
        </MenuItem>
      </Menu>
    </>
  );
}
